import {useTranslation} from "react-i18next";
import {
  type ControlCardComboBoxOptionsQuery,
  ControlCardDeleteComboBoxOptionDocument,
  ControlCardSaveComboBoxOptionDocument,
  ControlCardUpdateComboBoxOptionDocument
} from "app/gql/graphqlSchema";
import {useCallback, useMemo} from "react";
import {useIonAlert} from "@ionic/react";
import type {ID, MaybeNil} from "app/utils/types";
import {query} from "app/gql/client";
import {z} from "zod";
import {zNormalizedString} from "app/utils/validator";
import {Deferred} from "app/utils/deferred";
import type {EditComboBoxOptionOutcome} from "app/employee/controlCard/types";

const addOption = async (listKey: string, value: string) => {
  const resp = await query(ControlCardSaveComboBoxOptionDocument, {input: {listKey, value}});
  return resp.controlCardSaveComboBoxOption;
};

const updateOption = async (listKey: string, optionId: ID, newValue: string) => {
  await query(ControlCardUpdateComboBoxOptionDocument, {input: {listKey, optionId, newValue}});
};

const deleteOption = async (listKey: string, optionId: ID) => {
  await query(ControlCardDeleteComboBoxOptionDocument, {input: {listKey, optionId}});
};

type Option = ControlCardComboBoxOptionsQuery["controlCardComboBoxOptions"][number];
const textExtractor = (option: Option) => option.value;
const keyExtractor = (option: Option) => option.id;

export const useEditComboBoxOption = (listKey: string) => {
  const {t} = useTranslation();

  const editSchema = useMemo(
    () =>
      z.object({
        value: zNormalizedString.pipe(z.string().min(1, t("g.required")))
      }),
    [t]
  );

  const [presentEditOption] = useIonAlert();
  const [presentConfirmation] = useIonAlert();

  const edit = useCallback(
    async (option: Option) => {
      const deferred = new Deferred<EditComboBoxOptionOutcome<Option>>();

      await presentEditOption({
        header: t("g.combo-box.edit-header"),
        inputs: [
          {
            name: "value",
            type: "text",
            value: option.value
          }
        ],
        buttons: [
          {
            text: t("g.cancel"),
            role: "cancel",
            handler: () => deferred.resolve({success: false})
          },
          {
            text: t("g.delete"),
            role: "destructive",
            handler: async () => {
              await presentConfirmation({
                header: t("g.combo-box.delete-title", {value: option.value}),
                buttons: [
                  {
                    text: t("g.cancel"),
                    role: "cancel",
                    handler: () => deferred.resolve({success: false})
                  },
                  {
                    text: t("g.delete"),
                    role: "destructive",
                    handler: async () => {
                      try {
                        await deleteOption(listKey, option.id);
                      } finally {
                        deferred.resolve({success: true, option});
                      }
                    }
                  }
                ]
              });
            }
          },
          {
            text: t("g.save"),
            handler: async (handlerValue) => {
              const parsedValue = editSchema.safeParse(handlerValue);
              if (parsedValue.success) {
                try {
                  const newValue = parsedValue.data.value;
                  await updateOption(listKey, option.id, newValue);
                  deferred.resolve({success: true, option: {...option, value: newValue}});
                } catch {
                  deferred.resolve({success: false});
                }
              } else {
                deferred.resolve({success: false});
              }
            }
          }
        ]
      });

      return deferred.promise;
    },
    [editSchema, listKey, presentConfirmation, presentEditOption, t]
  );

  const add = useCallback(
    async (defaultValue: MaybeNil<string>) => {
      const deferred = new Deferred<EditComboBoxOptionOutcome<Option>>();

      await presentEditOption({
        header: t("g.combo-box.add-header"),
        inputs: [
          {
            name: "value",
            type: "text",
            value: defaultValue
          }
        ],
        buttons: [
          {
            text: t("g.cancel"),
            role: "cancel",
            handler: () => deferred.resolve({success: false})
          },
          {
            text: t("g.save"),
            handler: async (handlerValue) => {
              const parsedValue = editSchema.safeParse(handlerValue);
              if (parsedValue.success) {
                try {
                  const newOption = await addOption(listKey, parsedValue.data.value);
                  deferred.resolve({success: true, option: newOption});
                } catch {
                  deferred.resolve({success: false});
                }
              } else {
                deferred.resolve({success: false});
              }
            }
          }
        ]
      });

      return deferred.promise;
    },
    [editSchema, listKey, presentEditOption, t]
  );

  return {editItem: edit, addItem: add, textExtractor, keyExtractor};
};
